(() => {
  const SKAIIY_BUNDLE_VERSION = '6.3.0';
  const BUNDLE_SELECTOR = '[data-skaiiy-native-bundle]';
  const PRODUCT_ROOT_SELECTOR = 'product-info';
  const catalogCache = new WeakMap();
  const initializedRoots = new WeakSet();
  const pickerParts = new WeakMap();
  const allowedCollectionsCache = new WeakMap();
  const currentProductCollectionsCache = new WeakMap();
  const collectionCatalogPromises = new Map();
  let pageLockState = null;

  const escapeHtml = value => String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const moneyToCents = value => {
    if (value == null || value === '') return 0;
    if (typeof value === 'number') return Number.isInteger(value) ? value : Math.round(value * 100);
    const cleaned = String(value).replace(/[^0-9.,-]/g, '').replace(/,/g, '');
    if (!cleaned) return 0;
    const number = Number(cleaned);
    if (!Number.isFinite(number)) return 0;
    return cleaned.includes('.') ? Math.round(number * 100) : Math.round(number);
  };

  const formatMoney = (cents, moneyFormat) => {
    const value = Number(cents || 0);
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(value, moneyFormat);
    }
    const amount = (value / 100).toFixed(2);
    return String(moneyFormat || '${{amount}}')
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(value / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(value / 100).toString());
  };

  const isVisible = element => {
    if (!element) return false;
    const styles = window.getComputedStyle(element);
    return styles.display !== 'none' && styles.visibility !== 'hidden' && element.getClientRects().length > 0;
  };

  const normalizeImage = image => {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return String(image.url || image.src || image.originalSrc || '');
  };

  const normalizeOptionNames = product => {
    const raw = product && (product.optionNames || product.options);
    if (!Array.isArray(raw)) return [];
    return raw.map(option => {
      if (typeof option === 'string') return option;
      return String(option && (option.name || option.title) || '');
    }).filter(Boolean);
  };

  const normalizeVariantOptions = variant => {
    if (Array.isArray(variant.options)) return variant.options.map(value => String(value));
    return [variant.option1, variant.option2, variant.option3]
      .filter(value => value != null && value !== '')
      .map(value => String(value));
  };

  const normalizeProduct = product => {
    if (!product) return null;
    const rawId = product.id == null ? '' : String(product.id);
    if (!rawId) return null;

    const optionNames = normalizeOptionNames(product);
    const productImage = normalizeImage(
      product.featured_image || product.image || (Array.isArray(product.images) ? product.images[0] : '')
    );

    const variants = Array.isArray(product.variants) ? product.variants.map(variant => ({
      id: Number(variant.id),
      title: String(variant.title || variant.name || 'Default Title'),
      available: variant.available !== false,
      price: moneyToCents(variant.price),
      options: normalizeVariantOptions(variant),
      image: normalizeImage(variant.featured_image || variant.image) || productImage
    })).filter(variant => variant.id) : [];

    const inferredNames = optionNames.length
      ? optionNames
      : Array.from({ length: Math.max(0, ...variants.map(variant => variant.options.length)) }, (_, index) => `Option ${index + 1}`);

    const handle = String(product.handle || '').trim();
    const url = product.url ? String(product.url) : (handle ? `/products/${handle}` : '');

    return {
      id: rawId,
      handle,
      title: String(product.title || ''),
      url,
      image: productImage,
      tags: Array.isArray(product.tags)
        ? product.tags.map(tag => String(tag).trim()).filter(Boolean)
        : String(product.tags || '').split(',').map(tag => tag.trim()).filter(Boolean),
      optionNames: inferredNames,
      variants,
      price: moneyToCents(product.price || (variants[0] ? variants[0].price : 0)),
      collectionHandles: Array.isArray(product.collectionHandles) ? product.collectionHandles.map(String) : []
    };
  };

  const mergeProducts = products => {
    const unique = new Map();
    products.filter(Boolean).forEach(product => {
      const key = String(product.id);
      const previous = unique.get(key);
      if (!previous) {
        unique.set(key, product);
        return;
      }
      unique.set(key, {
        ...previous,
        ...product,
        variants: product.variants && product.variants.length ? product.variants : previous.variants,
        optionNames: product.optionNames && product.optionNames.length ? product.optionNames : previous.optionNames,
        tags: Array.from(new Set([...(previous.tags || []), ...(product.tags || [])])),
        collectionHandles: Array.from(new Set([...(previous.collectionHandles || []), ...(product.collectionHandles || [])]))
      });
    });
    return Array.from(unique.values());
  };

  const setCatalog = (bundle, products) => {
    const normalized = mergeProducts(products);
    catalogCache.set(bundle, normalized);
    return normalized;
  };

  const parseCatalog = bundle => {
    if (catalogCache.has(bundle)) return catalogCache.get(bundle);
    const products = Array.from(bundle.querySelectorAll('[data-skaiiy-catalog-product]')).map(node => {
      let variants = [];
      const variantScript = node.querySelector('[data-skaiiy-product-variants]');
      try {
        variants = JSON.parse(variantScript ? variantScript.textContent : '[]');
      } catch (error) {
        console.error('Skaiiy bundle variant data could not be parsed:', error);
      }
      return normalizeProduct({
        id: node.dataset.productId,
        title: node.dataset.productTitle,
        url: node.dataset.productUrl,
        featured_image: node.dataset.productImage,
        tags: String(node.dataset.productTags || '').split('||'),
        optionNames: String(node.dataset.productOptionNames || '').split('||').filter(Boolean),
        variants
      });
    });
    return setCatalog(bundle, products);
  };

  const addProductToCatalog = (bundle, product) => {
    const normalized = normalizeProduct(product);
    if (!normalized) return null;
    setCatalog(bundle, [...parseCatalog(bundle), normalized]);
    return normalized;
  };

  const getShopRoot = () => {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const slugifyHandle = value => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const normalizeCollectionList = value => {
    if (Array.isArray(value)) {
      return value.map(collection => {
        if (!collection) return null;
        const label = String(collection.label || collection.title || '').trim();
        const handle = String(collection.handle || '').trim();
        const id = collection.id == null ? '' : String(collection.id);
        return label && handle ? { id, label, handle } : null;
      }).filter(Boolean);
    }

    return String(value || '').split(/\r?\n/).map(line => {
      const [labelPart, handlePart] = line.split('|');
      const label = String(labelPart || '').trim();
      const handle = String(handlePart || '').trim() || slugifyHandle(label);
      return label && handle ? { id: '', label, handle } : null;
    }).filter(Boolean);
  };

  const readCollectionScript = (bundle, selector) => {
    const script = bundle.querySelector(selector);
    if (!script) return [];
    try {
      return normalizeCollectionList(JSON.parse(script.textContent || '[]'));
    } catch (error) {
      console.error('Skaiiy bundle collection data could not be parsed:', error);
      return normalizeCollectionList(script.textContent || '');
    }
  };

  const getAllowedCollections = bundle => {
    if (allowedCollectionsCache.has(bundle)) return allowedCollectionsCache.get(bundle);
    const result = readCollectionScript(bundle, '[data-skaiiy-allowed-collections]');
    allowedCollectionsCache.set(bundle, result);
    return result;
  };

  const getCurrentProductCollections = bundle => {
    if (currentProductCollectionsCache.has(bundle)) return currentProductCollectionsCache.get(bundle);
    const result = readCollectionScript(bundle, '[data-skaiiy-current-product-collections]');
    currentProductCollectionsCache.set(bundle, result);
    return result;
  };

  const getPreferredCollectionHandle = bundle => {
    const allowed = getAllowedCollections(bundle);
    if (!allowed.length) return '';

    const currentCollections = getCurrentProductCollections(bundle);
    const matching = allowed.find(allowedCollection => currentCollections.some(currentCollection => {
      if (allowedCollection.id && currentCollection.id) return allowedCollection.id === currentCollection.id;
      return allowedCollection.handle === currentCollection.handle;
    }));
    if (matching) return matching.handle;

    const configuredFallback = String(bundle.dataset.catalogCollectionHandle || '');
    if (allowed.some(collection => collection.handle === configuredFallback)) return configuredFallback;
    return allowed[0].handle;
  };


  const fetchAllCollectionProducts = async (handle, onProgress) => {
    const normalizedHandle = String(handle || '').trim();
    if (!normalizedHandle) return [];
    if (collectionCatalogPromises.has(normalizedHandle)) return collectionCatalogPromises.get(normalizedHandle);

    const promise = (async () => {
      const products = [];
      const limit = 250;
      for (let page = 1; page <= 100; page += 1) {
        if (typeof onProgress === 'function') onProgress(page, products.length);
        const url = `${getShopRoot()}collections/${encodeURIComponent(normalizedHandle)}/products.json?limit=${limit}&page=${page}`;
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Collection “${normalizedHandle}” could not be loaded.`);
        const payload = await response.json();
        const pageProducts = Array.isArray(payload.products) ? payload.products : [];
        pageProducts.forEach(rawProduct => {
          const normalized = normalizeProduct({ ...rawProduct, collectionHandles: [normalizedHandle] });
          if (normalized) products.push(normalized);
        });
        if (pageProducts.length < limit) break;
      }
      return mergeProducts(products);
    })();

    collectionCatalogPromises.set(normalizedHandle, promise);
    try {
      return await promise;
    } catch (error) {
      collectionCatalogPromises.delete(normalizedHandle);
      throw error;
    }
  };

  const hydrateProduct = async (bundleRoots, product) => {
    if (product && product.variants && product.variants.length && product.optionNames && product.optionNames.length) return product;
    if (!product || !product.url) return product;
    const cleanUrl = String(product.url).split('?')[0].replace(/\/$/, '');
    const response = await fetch(`${cleanUrl}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('This product could not be loaded.');
    const payload = await response.json();
    let normalized = null;
    bundleRoots.forEach(bundle => {
      normalized = addProductToCatalog(bundle, payload) || normalized;
    });
    return normalized || product;
  };

  const lockPageScroll = () => {
    if (pageLockState) return;
    const body = document.body;
    const html = document.documentElement;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    pageLockState = {
      scrollY,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow
    };
    html.classList.add('skaiiy-bundle-picker-open');
    body.classList.add('skaiiy-bundle-picker-open');
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
  };

  const unlockPageScroll = () => {
    if (!pageLockState) return;
    const body = document.body;
    const html = document.documentElement;
    const saved = pageLockState;
    pageLockState = null;
    html.classList.remove('skaiiy-bundle-picker-open');
    body.classList.remove('skaiiy-bundle-picker-open');
    html.style.overflow = saved.htmlOverflow;
    body.style.overflow = saved.bodyOverflow;
    body.style.position = saved.bodyPosition;
    body.style.top = saved.bodyTop;
    body.style.left = saved.bodyLeft;
    body.style.right = saved.bodyRight;
    body.style.width = saved.bodyWidth;
    window.scrollTo(0, saved.scrollY);
  };

  const initializeProduct = productRoot => {
    if (!productRoot || initializedRoots.has(productRoot)) return;
    const bundleRoots = Array.from(productRoot.querySelectorAll(BUNDLE_SELECTOR));
    if (!bundleRoots.length) return;
    initializedRoots.add(productRoot);

    const firstOption = bundleRoots[0].querySelector('[data-skaiiy-bundle-option].is-selected')
      || bundleRoots[0].querySelector('[data-skaiiy-bundle-option]');

    const state = {
      tierIndex: firstOption ? String(firstOption.dataset.tierIndex || '1') : '1',
      quantity: firstOption ? Math.max(1, Number(firstOption.dataset.quantity) || 1) : 1,
      discount: firstOption ? Math.max(0, Number(firstOption.dataset.discount) || 0) : 0,
      discountCode: firstOption ? String(firstOption.dataset.discountCode || '').trim() : '',
      additional: [],
      activePickerRoot: null,
      activeSlotIndex: null,
      activeProduct: null,
      activeFlow: null,
      pickerResults: [],
      pickerCatalog: [],
      pickerLoadToken: 0,
      searchTimer: null,
      lastFocusedElement: null,
      currentVariantOverride: null,
      adding: false
    };

    const sectionId = String(bundleRoots[0].dataset.sectionId || productRoot.dataset.section || '');
    const moneyFormat = bundleRoots[0].dataset.moneyFormat || '${{amount}}';
    const mixMatchEnabled = bundleRoots.some(bundle => bundle.dataset.mixMatchEnabled === 'true');
    const getVisibleBundle = () => bundleRoots.find(isVisible) || bundleRoots[0];
    const getParts = bundle => pickerParts.get(bundle) || {};
    const isBundleActive = () => mixMatchEnabled && state.quantity > 1;

    bundleRoots.forEach(parseCatalog);

    const addProductToAllCatalogs = product => {
      let normalized = null;
      bundleRoots.forEach(bundle => {
        normalized = addProductToCatalog(bundle, product) || normalized;
      });
      return normalized;
    };

    const getCatalogProduct = (bundle, productId) => {
      const id = String(productId == null ? '' : productId);
      return parseCatalog(bundle).find(product => String(product.id) === id) || null;
    };

    const getCurrentVariantInput = () => {
      const inputs = Array.from(productRoot.querySelectorAll(
        'form[action*="/cart/add"] input[name="id"], input[name="id"][data-product-select], input.product-variant-id'
      ));
      return inputs.find(input => isVisible(input.closest('form'))) || inputs[0] || null;
    };

    const getCurrentSelection = bundle => {
      const currentProductId = String(bundle.dataset.currentProductId || '');
      const product = getCatalogProduct(bundle, currentProductId) || {
        id: currentProductId,
        title: bundle.dataset.currentProductTitle || '',
        image: bundle.dataset.currentProductImage || '',
        optionNames: [],
        variants: []
      };
      const input = getCurrentVariantInput();
      const inputVariantId = Number(input && input.value ? input.value : bundle.dataset.currentVariantId || 0);
      const override = state.currentVariantOverride && Number(state.currentVariantOverride.id) === inputVariantId
        ? state.currentVariantOverride
        : null;
      const catalogVariant = product.variants.find(variant => variant.id === inputVariantId);
      const variant = catalogVariant || (override ? {
        id: Number(override.id),
        title: String(override.title || bundle.dataset.currentVariantTitle || 'Default Title'),
        available: override.available !== false,
        price: moneyToCents(override.price || bundle.dataset.currentVariantPrice || bundle.dataset.unitPrice || 0),
        options: Array.isArray(override.options) ? override.options.map(String) : [],
        image: normalizeImage(override.featured_image) || bundle.dataset.currentProductImage || ''
      } : null) || product.variants.find(item => item.available) || product.variants[0] || {
        id: inputVariantId,
        title: bundle.dataset.currentVariantTitle || 'Default Title',
        available: true,
        price: Number(bundle.dataset.currentVariantPrice || bundle.dataset.unitPrice || 0),
        options: [],
        image: bundle.dataset.currentProductImage || ''
      };
      return {
        product,
        variant,
        productId: String(product.id),
        variantId: Number(variant.id),
        title: product.title,
        variantTitle: variant.title,
        image: variant.image || product.image,
        price: Number(variant.price || 0),
        available: variant.available !== false
      };
    };

    const getAdditionalEntry = index => state.additional[index] || null;

    const getAdditionalSelection = (bundle, index) => {
      const entry = getAdditionalEntry(index);
      if (!entry || !entry.variantId) return null;
      const product = getCatalogProduct(bundle, entry.productId);
      if (!product) return null;
      const variant = product.variants.find(item => item.id === Number(entry.variantId));
      if (!variant) return null;
      return {
        product,
        variant,
        productId: String(product.id),
        variantId: variant.id,
        title: product.title,
        variantTitle: variant.title,
        image: variant.image || product.image,
        price: Number(variant.price || 0),
        available: variant.available !== false
      };
    };

    const ensureSelectionLength = () => {
      const required = Math.max(0, state.quantity - 1);
      if (state.additional.length > required) state.additional = state.additional.slice(0, required);
      while (state.additional.length < required) state.additional.push(null);
    };

    const setNativeQuantity = quantity => {
      const normalizedQuantity = Math.max(1, Number(quantity) || 1);
      const nativeQuantity = mixMatchEnabled && normalizedQuantity > 1 ? 1 : normalizedQuantity;
      productRoot.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
        let input = form.querySelector('input[name="quantity"]');
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'quantity';
          input.setAttribute('data-skaiiy-bundle-quantity-input', '');
          form.appendChild(input);
        }
        input.value = String(nativeQuantity);
        input.setAttribute('value', String(nativeQuantity));
      });
      productRoot.dataset.skaiiyBundleQuantity = String(normalizedQuantity);
    };

    const getOptionMeta = product => {
      const names = Array.isArray(product.optionNames) ? product.optionNames : [];
      const indexes = names.map((_, index) => index);
      const styleIndex = indexes.find(index => /style/i.test(names[index]))
        ?? indexes.find(index => /(color|colour|design|character)/i.test(names[index]));
      const sizeIndex = indexes.find(index => /size/i.test(names[index]));
      const order = [];
      if (styleIndex != null) order.push(styleIndex);
      if (sizeIndex != null && sizeIndex !== styleIndex) order.push(sizeIndex);
      indexes.forEach(index => { if (!order.includes(index)) order.push(index); });
      return { names, styleIndex, sizeIndex, order };
    };

    const optionValue = (variant, index) => String((variant.options || [])[index] || '');

    const matchingVariants = (product, selected) => product.variants.filter(variant => {
      if (!variant.available) return false;
      return Object.entries(selected).every(([index, value]) => optionValue(variant, Number(index)) === String(value));
    });

    const createVariantFlow = (product, options = {}) => {
      const meta = getOptionMeta(product);
      const available = product.variants.filter(variant => variant.available);
      const selected = {};
      const locked = new Set();
      let sourceVariant = null;
      if (options.variantId) sourceVariant = available.find(variant => variant.id === Number(options.variantId)) || null;
      if (options.lockStyle && sourceVariant && meta.styleIndex != null) {
        selected[meta.styleIndex] = optionValue(sourceVariant, meta.styleIndex);
        locked.add(meta.styleIndex);
      }
      if (options.lockStyleValue != null && meta.styleIndex != null) {
        selected[meta.styleIndex] = String(options.lockStyleValue);
        locked.add(meta.styleIndex);
      }
      const flow = {
        product,
        meta,
        selected,
        locked,
        currentIndex: null,
        variant: null,
        lastIndex: null,
        allowStyleReset: options.allowStyleReset !== false
      };

      const advance = () => {
        flow.variant = null;
        while (true) {
          const nextIndex = flow.meta.order.find(index => !Object.prototype.hasOwnProperty.call(flow.selected, index));
          if (nextIndex == null) {
            flow.currentIndex = null;
            flow.variant = matchingVariants(flow.product, flow.selected)[0] || null;
            return;
          }
          const values = Array.from(new Set(matchingVariants(flow.product, flow.selected)
            .map(variant => optionValue(variant, nextIndex)).filter(Boolean)));
          if (values.length === 1) {
            flow.selected[nextIndex] = values[0];
            flow.lastIndex = nextIndex;
            continue;
          }
          flow.currentIndex = nextIndex;
          return;
        }
      };

      flow.advance = advance;
      if (!meta.order.length || available.length <= 1) {
        flow.variant = available[0] || null;
        flow.currentIndex = null;
      } else {
        advance();
      }
      return flow;
    };

    const chooseFlowValue = (flow, value) => {
      if (!flow || flow.currentIndex == null || !value) return;
      flow.selected[flow.currentIndex] = String(value);
      flow.lastIndex = flow.currentIndex;
      flow.currentIndex = null;
      flow.advance();
    };

    const resetFlowStyle = flow => {
      if (!flow) return;
      flow.selected = {};
      flow.locked = new Set();
      flow.currentIndex = null;
      flow.variant = null;
      flow.lastIndex = null;
      flow.advance();
    };

    const getFlowValues = flow => {
      if (!flow || flow.currentIndex == null) return [];
      return Array.from(new Set(matchingVariants(flow.product, flow.selected)
        .map(variant => optionValue(variant, flow.currentIndex)).filter(Boolean)));
    };

    const getOptionLabel = (flow, index) => {
      const actual = flow.meta.names[index] || `Option ${index + 1}`;
      if (index === flow.meta.styleIndex) return actual || 'Style';
      if (index === flow.meta.sizeIndex) return actual || 'Size';
      return actual;
    };

    const variantSummaryHtml = (product, variant) => {
      if (!variant || variant.title === 'Default Title') return '<span>Standard option</span>';
      const names = product.optionNames || [];
      return variant.options.map((value, index) => {
        const label = names[index] || `Option ${index + 1}`;
        return `<span><small>${escapeHtml(label)}</small>${escapeHtml(value)}</span>`;
      }).join('');
    };

    const pendingPrice = (bundle, index, fallback) => {
      const entry = getAdditionalEntry(index);
      if (!entry) return fallback;
      const product = getCatalogProduct(bundle, entry.productId);
      if (!product) return fallback;
      if (entry.variantId) {
        const variant = product.variants.find(item => item.id === Number(entry.variantId));
        return variant ? Number(variant.price || fallback) : fallback;
      }
      return Number((product.variants.find(variant => variant.available) || {}).price || fallback);
    };

    const getTierPriceBasis = (bundle, quantity) => {
      const current = getCurrentSelection(bundle);
      let originalTotal = current.price * quantity;
      if (mixMatchEnabled && quantity === state.quantity && quantity > 1) {
        originalTotal = current.price;
        for (let index = 0; index < quantity - 1; index += 1) {
          originalTotal += pendingPrice(bundle, index, current.price);
        }
      }
      return originalTotal;
    };

    const updateOptionPrices = bundle => {
      bundle.querySelectorAll('[data-skaiiy-bundle-option]').forEach(option => {
        const quantity = Math.max(1, Number(option.dataset.quantity) || 1);
        const discount = Math.min(100, Math.max(0, Number(option.dataset.discount) || 0));
        const originalTotal = getTierPriceBasis(bundle, quantity);
        const discountedTotal = Math.round(originalTotal * (100 - discount) / 100);
        const eachPrice = Math.round(discountedTotal / quantity);
        const saving = originalTotal - discountedTotal;
        const totalElement = option.querySelector('[data-bundle-total]');
        const eachElement = option.querySelector('[data-bundle-each]');
        const compareElement = option.querySelector('[data-bundle-compare]');
        const savingElement = option.querySelector('[data-bundle-saving]');
        if (totalElement) totalElement.textContent = formatMoney(discountedTotal, moneyFormat);
        if (eachElement) eachElement.textContent = `${formatMoney(eachPrice, moneyFormat)} each`;
        if (compareElement) {
          compareElement.textContent = formatMoney(originalTotal, moneyFormat);
          compareElement.hidden = discount <= 0;
        }
        if (savingElement) {
          savingElement.textContent = `Save ${formatMoney(saving, moneyFormat)}`;
          savingElement.hidden = discount <= 0;
        }
      });
    };

    const renderRepeatCurrentPending = (bundle, slotIndex, entry, product) => {
      const meta = getOptionMeta(product);
      const current = getCurrentSelection(bundle);
      const styleValue = entry.lockedStyleValue || (meta.styleIndex != null ? optionValue(current.variant, meta.styleIndex) : '');
      const candidates = product.variants.filter(variant => {
        if (!variant.available) return false;
        return meta.styleIndex == null || !styleValue || optionValue(variant, meta.styleIndex) === styleValue;
      });
      const optionMap = new Map();
      candidates.forEach(variant => {
        const label = meta.sizeIndex != null ? optionValue(variant, meta.sizeIndex) : variant.title;
        if (label && !optionMap.has(label)) optionMap.set(label, variant);
      });
      const selectLabel = meta.sizeIndex != null ? 'Choose size' : 'Choose option';
      const options = Array.from(optionMap.entries()).map(([label, variant]) =>
        `<option value="${variant.id}">${escapeHtml(label)} · ${escapeHtml(formatMoney(variant.price, moneyFormat))}</option>`
      ).join('');
      return `
        <div class="skaiiy-native-bundle__slot is-selected is-pending" data-skaiiy-bundle-slot="${slotIndex}">
          <span class="skaiiy-native-bundle__slot-number">ITEM ${slotIndex + 1}</span>
          <div class="skaiiy-native-bundle__slot-main">
            <div class="skaiiy-native-bundle__slot-image">
              ${product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" width="220" height="220">` : ''}
            </div>
            <div class="skaiiy-native-bundle__slot-copy">
              <strong>${escapeHtml(product.title)}</strong>
              ${styleValue ? `<p class="skaiiy-native-bundle__locked-style"><small>${escapeHtml(meta.names[meta.styleIndex] || 'Style')}</small>${escapeHtml(styleValue)}</p>` : ''}
              <label class="skaiiy-native-bundle__repeat-field">
                <span>${escapeHtml(selectLabel)}</span>
                <span class="skaiiy-native-bundle__select-shell skaiiy-native-bundle__select-shell--inline">
                  <select data-skaiiy-repeat-variant-select data-slot-index="${slotIndex}" aria-label="${escapeHtml(selectLabel)}">
                    <option value="">${escapeHtml(selectLabel)}</option>
                    ${options}
                  </select>
                  <span class="skaiiy-native-bundle__select-chevron" aria-hidden="true"></span>
                </span>
              </label>
              <div class="skaiiy-native-bundle__slot-actions">
                <button type="button" class="skaiiy-native-bundle__slot-action" data-skaiiy-open-picker data-slot-index="${slotIndex}">Choose a different product</button>
                <button type="button" class="skaiiy-native-bundle__slot-action" data-skaiiy-remove-slot data-slot-index="${slotIndex}">Remove</button>
              </div>
            </div>
          </div>
        </div>`;
    };

    const renderSlot = (bundle, slotIndex) => {
      const current = getCurrentSelection(bundle);
      if (slotIndex === 0) {
        return `
          <div class="skaiiy-native-bundle__slot is-current" data-skaiiy-bundle-slot="0">
            <span class="skaiiy-native-bundle__slot-number">ITEM 1</span>
            <div class="skaiiy-native-bundle__slot-main">
              <div class="skaiiy-native-bundle__slot-image">
                ${current.image ? `<img src="${escapeHtml(current.image)}" alt="${escapeHtml(current.title)}" width="220" height="220">` : ''}
              </div>
              <div class="skaiiy-native-bundle__slot-copy">
                <strong>${escapeHtml(current.title)}</strong>
                <div class="skaiiy-native-bundle__selected-options">${variantSummaryHtml(current.product, current.variant)}</div>
                <span class="skaiiy-native-bundle__slot-help">Change this item using the product options above.</span>
              </div>
            </div>
          </div>`;
      }

      const entry = getAdditionalEntry(slotIndex - 1);
      if (!entry) {
        return `
          <div class="skaiiy-native-bundle__slot is-empty" data-skaiiy-bundle-slot="${slotIndex}">
            <span class="skaiiy-native-bundle__slot-number">ITEM ${slotIndex + 1}</span>
            <button type="button" class="skaiiy-native-bundle__empty-slot" data-skaiiy-open-picker data-slot-index="${slotIndex}">
              <span class="skaiiy-native-bundle__empty-slot-plus" aria-hidden="true">+</span>
              <span><strong>Choose another product</strong><small>Search all products in the bundle collections</small></span>
            </button>
            <button type="button" class="skaiiy-native-bundle__repeat-current-button" data-skaiiy-add-current-product data-slot-index="${slotIndex}">
              <span>OR ADD MORE OF</span>
              <strong>${escapeHtml(current.title)}</strong>
            </button>
          </div>`;
      }

      const product = getCatalogProduct(bundle, entry.productId);
      if (!product) return '';
      if (!entry.variantId) return renderRepeatCurrentPending(bundle, slotIndex, entry, product);
      const selection = getAdditionalSelection(bundle, slotIndex - 1);
      if (!selection) return renderRepeatCurrentPending(bundle, slotIndex, entry, product);

      return `
        <div class="skaiiy-native-bundle__slot is-selected" data-skaiiy-bundle-slot="${slotIndex}">
          <span class="skaiiy-native-bundle__slot-number">ITEM ${slotIndex + 1}</span>
          <div class="skaiiy-native-bundle__slot-main">
            <div class="skaiiy-native-bundle__slot-image">
              ${selection.image ? `<img src="${escapeHtml(selection.image)}" alt="${escapeHtml(selection.title)}" width="220" height="220">` : ''}
            </div>
            <div class="skaiiy-native-bundle__slot-copy">
              <strong>${escapeHtml(selection.title)}</strong>
              <div class="skaiiy-native-bundle__selected-options">${variantSummaryHtml(selection.product, selection.variant)}</div>
              <div class="skaiiy-native-bundle__slot-actions">
                <button type="button" class="skaiiy-native-bundle__slot-action" data-skaiiy-edit-slot-variant data-slot-index="${slotIndex}">Change style / size</button>
                <button type="button" class="skaiiy-native-bundle__slot-action" data-skaiiy-open-picker data-slot-index="${slotIndex}">Change product</button>
                <button type="button" class="skaiiy-native-bundle__slot-action" data-skaiiy-remove-slot data-slot-index="${slotIndex}">Remove</button>
              </div>
            </div>
          </div>
        </div>`;
    };

    const renderBuilder = bundle => {
      const builder = bundle.querySelector('[data-skaiiy-bundle-builder]');
      if (!builder) return;
      const shouldShow = mixMatchEnabled && state.quantity > 1;
      builder.hidden = !shouldShow;
      if (!shouldShow) return;
      const slots = builder.querySelector('[data-skaiiy-builder-slots]');
      if (slots) {
        let html = '';
        for (let index = 0; index < state.quantity; index += 1) html += renderSlot(bundle, index);
        slots.innerHTML = html;
      }
      const selectedCount = 1 + state.additional.slice(0, state.quantity - 1).filter(entry => entry && entry.variantId).length;
      const progress = builder.querySelector('[data-skaiiy-builder-progress]');
      if (progress) progress.textContent = `${selectedCount} / ${state.quantity} selected`;
    };

    const clearErrors = () => {
      bundleRoots.forEach(bundle => {
        const error = bundle.querySelector('[data-skaiiy-bundle-error]');
        if (error) {
          error.hidden = true;
          error.textContent = '';
        }
      });
    };

    const showError = message => {
      bundleRoots.forEach(bundle => {
        const error = bundle.querySelector('[data-skaiiy-bundle-error]');
        if (error) {
          error.hidden = false;
          error.textContent = message;
        }
      });
    };

    const render = () => {
      ensureSelectionLength();
      setNativeQuantity(state.quantity);
      bundleRoots.forEach(bundle => {
        bundle.querySelectorAll('[data-skaiiy-bundle-option]').forEach(option => {
          const selected = String(option.dataset.tierIndex || '') === state.tierIndex;
          option.classList.toggle('is-selected', selected);
          option.setAttribute('aria-checked', selected ? 'true' : 'false');
        });
        renderBuilder(bundle);
        updateOptionPrices(bundle);
      });
      syncBundleCheckoutControls();
    };

    const setTier = option => {
      state.tierIndex = String(option.dataset.tierIndex || '1');
      state.quantity = Math.max(1, Number(option.dataset.quantity) || 1);
      state.discount = Math.max(0, Number(option.dataset.discount) || 0);
      state.discountCode = String(option.dataset.discountCode || '').trim();
      clearErrors();
      render();
    };

    const populateCollectionFilter = (bundle, picker, options = {}) => {
      const select = picker.querySelector('[data-skaiiy-picker-collection]');
      if (!select) return;

      const collections = getAllowedCollections(bundle);
      const showAllSelected = bundle.dataset.showAllPickerCollections === 'true' && collections.length > 1;
      const previousValue = String(select.value || '');
      select.innerHTML = '';

      if (showAllSelected) {
        const allOption = document.createElement('option');
        allOption.value = '';
        allOption.textContent = 'All selected categories';
        select.appendChild(allOption);
      }

      collections.forEach(collection => {
        const option = document.createElement('option');
        option.value = collection.handle;
        option.textContent = collection.label;
        select.appendChild(option);
      });

      const preferred = getPreferredCollectionHandle(bundle);
      const validValues = Array.from(select.options).map(option => option.value);
      if (options.resetToDefault || select.dataset.initialized !== 'true') {
        select.value = validValues.includes(preferred) ? preferred : (validValues[0] || '');
      } else if (validValues.includes(previousValue)) {
        select.value = previousValue;
      } else {
        select.value = validValues.includes(preferred) ? preferred : (validValues[0] || '');
      }

      select.disabled = collections.length <= 1 && !showAllSelected;
      select.dataset.initialized = 'true';
    };


    const setPickerStatus = (picker, options = {}) => {
      const loadingElement = picker.querySelector('[data-skaiiy-picker-loading]');
      const countElement = picker.querySelector('[data-skaiiy-picker-count]');
      const statusElement = picker.querySelector('[data-skaiiy-picker-status]');
      if (loadingElement && Object.prototype.hasOwnProperty.call(options, 'loading')) loadingElement.hidden = !options.loading;
      if (countElement && Object.prototype.hasOwnProperty.call(options, 'count')) {
        const count = Number(options.count || 0);
        countElement.textContent = `${count} product${count === 1 ? '' : 's'}`;
      }
      if (statusElement && Object.prototype.hasOwnProperty.call(options, 'message')) {
        const message = String(options.message || '');
        statusElement.textContent = message;
        statusElement.hidden = !message;
      }
    };

    const renderProductCards = (picker, products) => {
      const grid = picker.querySelector('[data-skaiiy-picker-grid]');
      const empty = picker.querySelector('[data-skaiiy-picker-empty]');
      if (!grid) return;
      state.pickerResults = products;
      grid.innerHTML = products.map((product, index) => {
        const availableVariants = product.variants.filter(variant => variant.available);
        const lowestPrice = availableVariants.length
          ? Math.min(...availableVariants.map(variant => variant.price))
          : Number(product.price || 0);
        return `
          <button type="button" class="skaiiy-native-bundle__picker-product" data-skaiiy-pick-product data-result-index="${index}">
            <span class="skaiiy-native-bundle__picker-product-image">
              ${product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" width="240" height="280" loading="lazy">` : '<span class="skaiiy-native-bundle__picker-product-placeholder">No image</span>'}
            </span>
            <span class="skaiiy-native-bundle__picker-product-copy">
              <strong>${escapeHtml(product.title)}</strong>
              <span>${lowestPrice ? `From ${escapeHtml(formatMoney(lowestPrice, moneyFormat))}` : 'Choose options'}</span>
            </span>
          </button>`;
      }).join('');
      if (empty) empty.hidden = products.length > 0;
      setPickerStatus(picker, { count: products.length });
    };

    const filterPickerProducts = picker => {
      const query = String((picker.querySelector('[data-skaiiy-picker-search]') || {}).value || '').trim().toLowerCase();
      return state.pickerCatalog.filter(product => {
        const hasAvailable = product.variants.some(variant => variant.available);
        const searchable = `${product.title} ${product.tags.join(' ')}`.toLowerCase();
        return hasAvailable && (!query || searchable.includes(query));
      });
    };

    const renderFilteredCatalog = picker => {
      const products = filterPickerProducts(picker);
      renderProductCards(picker, products);
      if (!products.length && state.pickerCatalog.length) setPickerStatus(picker, { message: 'No products match your search.' });
      else setPickerStatus(picker, { message: '' });
    };

    const loadPickerCatalog = async (bundle, picker) => {
      const token = ++state.pickerLoadToken;
      const select = picker.querySelector('[data-skaiiy-picker-collection]');
      const selectedHandle = String(select ? select.value : '');
      const allowed = getAllowedCollections(bundle);
      const targets = selectedHandle
        ? allowed.filter(item => item.handle === selectedHandle)
        : allowed;
      const localProducts = parseCatalog(bundle);
      const loadingMessage = selectedHandle
        ? 'Loading every product in this category…'
        : 'Loading every product from the selected categories…';
      setPickerStatus(picker, { loading: true, count: 0, message: loadingMessage });
      renderProductCards(picker, []);

      const loaded = [];
      const failures = [];
      if (!targets.length) {
        state.pickerCatalog = mergeProducts(localProducts);
        setPickerStatus(picker, {
          loading: false,
          count: state.pickerCatalog.length,
          message: 'Select category collections in the bundle block settings to load more products.'
        });
        renderFilteredCatalog(picker);
        return;
      }

      for (let index = 0; index < targets.length; index += 1) {
        const collection = targets[index];
        try {
          const products = await fetchAllCollectionProducts(collection.handle, (page, count) => {
            if (token !== state.pickerLoadToken) return;
            setPickerStatus(picker, {
              loading: true,
              count: loaded.length + count,
              message: `Loading ${collection.label}${page > 1 ? `, page ${page}` : ''}…`
            });
          });
          loaded.push(...products);
        } catch (error) {
          console.error('Skaiiy bundle collection load failed:', error);
          failures.push(collection.label);
        }
        if (token !== state.pickerLoadToken) return;
      }

      const merged = mergeProducts(loaded.length ? loaded : localProducts);
      merged.forEach(addProductToAllCatalogs);
      state.pickerCatalog = merged;
      if (token !== state.pickerLoadToken) return;
      setPickerStatus(picker, {
        loading: false,
        count: merged.length,
        message: failures.length ? `Could not load: ${failures.join(', ')}. Check those collection handles in the theme editor.` : ''
      });
      renderFilteredCatalog(picker);
    };

    const queueProductSearch = picker => {
      window.clearTimeout(state.searchTimer);
      state.searchTimer = window.setTimeout(() => renderFilteredCatalog(picker), 120);
    };

    const renderPickerFlow = picker => {
      const flow = state.activeFlow;
      const select = picker.querySelector('[data-skaiiy-progressive-select]');
      const label = picker.querySelector('[data-skaiiy-progressive-label]');
      const summary = picker.querySelector('[data-skaiiy-progressive-summary]');
      const reset = picker.querySelector('[data-skaiiy-progressive-reset]');
      const confirm = picker.querySelector('[data-skaiiy-picker-confirm]');
      const price = picker.querySelector('[data-skaiiy-picker-price]');
      const image = picker.querySelector('[data-skaiiy-picker-image]');
      if (!flow || !select) return;

      if (flow.currentIndex != null) {
        const optionLabel = getOptionLabel(flow, flow.currentIndex);
        if (label) label.textContent = `Choose ${optionLabel.toLowerCase()}`;
        const values = getFlowValues(flow);
        select.disabled = values.length === 0;
        select.innerHTML = `<option value="">Choose ${escapeHtml(optionLabel.toLowerCase())}</option>`
          + values.map(value => `<option value="${escapeHtml(value)}">${escapeHtml(value)}</option>`).join('');
      } else if (flow.variant) {
        if (label) label.textContent = 'Selected options';
        select.disabled = false;
        select.innerHTML = `<option value="${flow.variant.id}" selected>${escapeHtml(flow.variant.title === 'Default Title' ? 'Standard option' : flow.variant.title)}</option>`;
      } else {
        if (label) label.textContent = 'No available options';
        select.disabled = true;
        select.innerHTML = '<option value="">Sold out</option>';
      }

      const selectedParts = Object.entries(flow.selected).sort((a, b) => Number(a[0]) - Number(b[0])).map(([index, value]) => {
        const name = flow.meta.names[Number(index)] || `Option ${Number(index) + 1}`;
        return `${name}: ${value}`;
      });
      if (summary) summary.textContent = selectedParts.join(' · ');
      if (reset) {
        const canReset = flow.allowStyleReset && flow.meta.styleIndex != null && Object.prototype.hasOwnProperty.call(flow.selected, flow.meta.styleIndex);
        reset.hidden = !canReset;
      }
      if (confirm) confirm.disabled = !flow.variant;
      const candidates = flow.variant ? [flow.variant] : matchingVariants(flow.product, flow.selected);
      const preview = candidates[0] || null;
      if (preview) {
        if (price) price.textContent = formatMoney(preview.price, moneyFormat);
        if (image) image.src = preview.image || flow.product.image || '';
      }
    };

    const showProductStep = (bundle, picker, options = {}) => {
      const productsStep = picker.querySelector('[data-skaiiy-picker-products]');
      const variantStep = picker.querySelector('[data-skaiiy-picker-variant]');
      const back = picker.querySelector('[data-skaiiy-picker-back]');
      const subtitle = picker.querySelector('[data-skaiiy-picker-subtitle]');
      if (productsStep) productsStep.hidden = false;
      if (variantStep) variantStep.hidden = true;
      if (back) back.hidden = true;
      if (subtitle) subtitle.textContent = 'Search and choose a product.';
      state.activeProduct = null;
      state.activeFlow = null;
      populateCollectionFilter(bundle, picker, { resetToDefault: options.resetCollection === true });
      loadPickerCatalog(bundle, picker);
    };

    const showVariantStep = async (bundle, picker, product, options = {}) => {
      const productsStep = picker.querySelector('[data-skaiiy-picker-products]');
      const variantStep = picker.querySelector('[data-skaiiy-picker-variant]');
      const back = picker.querySelector('[data-skaiiy-picker-back]');
      const title = picker.querySelector('[data-skaiiy-picker-title]');
      const image = picker.querySelector('[data-skaiiy-picker-image]');
      const price = picker.querySelector('[data-skaiiy-picker-price]');
      const subtitle = picker.querySelector('[data-skaiiy-picker-subtitle]');
      const confirm = picker.querySelector('[data-skaiiy-picker-confirm]');
      if (productsStep) productsStep.hidden = true;
      if (variantStep) variantStep.hidden = false;
      if (back) back.hidden = false;
      if (subtitle) subtitle.textContent = 'Choose the style first, then the available size.';
      if (confirm) confirm.disabled = true;
      if (title) title.textContent = product.title;
      if (price) price.textContent = 'Loading options...';
      if (image) {
        image.src = product.image || '';
        image.alt = product.title;
      }
      try {
        const hydrated = await hydrateProduct(bundleRoots, product);
        state.activeProduct = hydrated;
        state.activeFlow = createVariantFlow(hydrated, options);
        if (title) title.textContent = hydrated.title;
        renderPickerFlow(picker);
      } catch (error) {
        console.error('Skaiiy bundle product load failed:', error);
        state.activeProduct = null;
        state.activeFlow = null;
        if (price) price.textContent = 'Could not load product options.';
      }
    };

    const focusableElements = picker => Array.from(picker.querySelectorAll(
      'button:not([disabled]):not([hidden]), input:not([disabled]):not([hidden]), select:not([disabled]):not([hidden]), a[href], [tabindex]:not([tabindex="-1"])'
    )).filter(isVisible);

    const openPicker = (bundle, slotIndex, directProduct = null, flowOptions = {}) => {
      const { picker, overlay } = getParts(bundle);
      if (!picker || !overlay) return;
      if (state.activePickerRoot && state.activePickerRoot !== bundle) {
        const previous = getParts(state.activePickerRoot);
        if (previous.picker) previous.picker.classList.remove('is-open');
        if (previous.overlay) previous.overlay.hidden = true;
      }
      state.lastFocusedElement = document.activeElement;
      state.activePickerRoot = bundle;
      state.activeSlotIndex = Number(slotIndex);
      const search = picker.querySelector('[data-skaiiy-picker-search]');
      if (search) search.value = '';
      overlay.hidden = false;
      picker.classList.add('is-open');
      picker.setAttribute('aria-hidden', 'false');
      lockPageScroll();
      if (directProduct) showVariantStep(bundle, picker, directProduct, flowOptions);
      else showProductStep(bundle, picker, { resetCollection: true });
      window.requestAnimationFrame(() => {
        picker.focus({ preventScroll: true });
        if (!directProduct && window.innerWidth >= 750) {
          window.setTimeout(() => {
            const input = picker.querySelector('[data-skaiiy-picker-search]');
            if (input) input.focus({ preventScroll: true });
          }, 120);
        }
      });
    };

    const closePicker = bundle => {
      const targetBundle = bundle || state.activePickerRoot;
      if (!targetBundle) return;
      const { picker, overlay } = getParts(targetBundle);
      if (picker) {
        picker.classList.remove('is-open');
        picker.setAttribute('aria-hidden', 'true');
      }
      if (overlay) overlay.hidden = true;
      state.pickerLoadToken += 1;
      unlockPageScroll();
      const restoreFocus = state.lastFocusedElement;
      state.activePickerRoot = null;
      state.activeSlotIndex = null;
      state.activeProduct = null;
      state.activeFlow = null;
      state.lastFocusedElement = null;
      if (restoreFocus && typeof restoreFocus.focus === 'function') {
        window.setTimeout(() => restoreFocus.focus({ preventScroll: true }), 40);
      }
    };

    const validateBundle = bundle => {
      const current = getCurrentSelection(bundle);
      if (!current.variantId || !current.available) return { valid: false, message: 'Choose an available option for the current product.' };
      if (!mixMatchEnabled || state.quantity <= 1) return { valid: true };
      for (let index = 0; index < state.quantity - 1; index += 1) {
        const entry = state.additional[index];
        if (!entry || !entry.variantId) {
          const remaining = state.additional.slice(0, state.quantity - 1).filter(item => !item || !item.variantId).length;
          return { valid: false, message: `Choose ${remaining} more item${remaining === 1 ? '' : 's'} to complete your bundle.`, emptySlot: index + 1 };
        }
        const selection = getAdditionalSelection(bundle, index);
        if (!selection || !selection.available) return { valid: false, message: 'One selected variant is no longer available.', emptySlot: index + 1 };
      }
      return { valid: true };
    };

    const buildCartItems = bundle => {
      const expectedQuantity = Math.max(1, Number(state.quantity) || 1);
      const selections = [getCurrentSelection(bundle)];

      for (let index = 0; index < expectedQuantity - 1; index += 1) {
        const selection = getAdditionalSelection(bundle, index);
        if (!selection || !selection.variantId) {
          throw new Error(`Bundle item ${index + 2} is missing. Choose its product and variant before continuing.`);
        }
        selections.push(selection);
      }

      if (selections.length !== expectedQuantity) {
        throw new Error(`The bundle has ${selections.length} selected item${selections.length === 1 ? '' : 's'}, but ${expectedQuantity} are required.`);
      }

      const grouped = new Map();
      selections.forEach(selection => {
        const variantId = Number(selection.variantId);
        if (!Number.isFinite(variantId) || variantId <= 0) {
          throw new Error('One bundle item has an invalid variant. Please select it again.');
        }
        grouped.set(variantId, (grouped.get(variantId) || 0) + 1);
      });

      const bundleReference = `SKAIIY-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`.toUpperCase();
      const privateProperties = {
        '_Skaiiy bundle': `Buy ${expectedQuantity}`,
        '_Skaiiy bundle discount': `${state.discount}%`,
        '_Skaiiy bundle reference': bundleReference
      };
      const items = Array.from(grouped.entries()).map(([id, quantity]) => ({
        id,
        quantity,
        properties: { ...privateProperties }
      }));
      const requestQuantity = items.reduce((total, item) => total + Number(item.quantity || 0), 0);
      if (requestQuantity !== expectedQuantity) {
        throw new Error(`The bundle request contains ${requestQuantity} item${requestQuantity === 1 ? '' : 's'} instead of ${expectedQuantity}.`);
      }
      return items;
    };

    const syncBundleCheckoutControls = () => {
      const active = isBundleActive();
      productRoot.classList.toggle('skaiiy-bundle-purchase-active', active);
      productRoot.dataset.skaiiyBundlePurchaseActive = active ? 'true' : 'false';

      productRoot.querySelectorAll('.shopify-payment-button').forEach(paymentContainer => {
        paymentContainer.classList.toggle('skaiiy-native-bundle-payment-hidden', active);
        const host = paymentContainer.parentElement || paymentContainer;
        let replacement = host.querySelector(':scope > [data-skaiiy-bundle-checkout]');
        if (!replacement) {
          replacement = document.createElement('button');
          replacement.type = 'button';
          replacement.className = 'skaiiy-native-bundle__checkout-button';
          replacement.setAttribute('data-skaiiy-bundle-checkout', '');
          replacement.textContent = 'BUY BUNDLE NOW';
          host.insertBefore(replacement, paymentContainer);
        }
        replacement.hidden = !active;
        replacement.disabled = !active;
        replacement.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
    };

    const discountApplicationMatches = (application, code) => {
      if (!application || !code) return false;
      const normalizedCode = String(code).trim().toLowerCase();
      const candidates = [
        application.code,
        application.title,
        application.description,
        application.discount_application && application.discount_application.title,
        application.discount_application && application.discount_application.code
      ];
      return candidates.some(value => String(value || '').trim().toLowerCase() === normalizedCode);
    };

    const cartShowsDiscountCode = (cartData, code) => {
      if (!cartData || !code) return false;
      const normalizedCode = String(code).trim().toLowerCase();
      const discountCodes = Array.isArray(cartData.discount_codes) ? cartData.discount_codes : [];
      if (discountCodes.some(item => {
        const itemCode = typeof item === 'string' ? item : item && item.code;
        const applicable = typeof item === 'object' && item != null && Object.prototype.hasOwnProperty.call(item, 'applicable')
          ? item.applicable !== false
          : true;
        return String(itemCode || '').trim().toLowerCase() === normalizedCode && applicable;
      })) return true;

      const cartApplications = Array.isArray(cartData.cart_level_discount_applications)
        ? cartData.cart_level_discount_applications
        : [];
      if (cartApplications.some(application => discountApplicationMatches(application, code))) return true;

      const items = Array.isArray(cartData.items) ? cartData.items : [];
      return items.some(item => {
        const discounts = Array.isArray(item.discounts) ? item.discounts : [];
        const allocations = Array.isArray(item.discount_allocations) ? item.discount_allocations : [];
        return discounts.some(discount => discountApplicationMatches(discount, code))
          || allocations.some(allocation => discountApplicationMatches(allocation, code));
      });
    };

    const applyBundleDiscount = async ({ code, cart, shopRoot }) => {
      const normalizedCode = String(code || '').trim();
      if (!normalizedCode) {
        throw new Error(`The Shopify discount code for Buy ${state.quantity} is empty in the bundle block settings.`);
      }

      const payload = { discount: normalizedCode };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        payload.sections = cart.getSectionsToRender().map(section => section.id);
        payload.sections_url = window.location.pathname;
      }

      const updateUrl = `${shopRoot.replace(/\/?$/, '/')}cart/update.js`;
      const response = await fetch(updateUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const responseText = await response.text();
      let result = {};
      try {
        result = responseText ? JSON.parse(responseText) : {};
      } catch (parseError) {
        throw new Error('Shopify returned an unreadable response while applying the bundle discount.');
      }
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || `Shopify could not apply ${normalizedCode}.`);
      }

      if (!cartShowsDiscountCode(result, normalizedCode) && Number(result.total_discount || 0) <= 0) {
        throw new Error(`The bundle products were added, but ${normalizedCode} did not apply. Create or fix that product discount code in Shopify Admin before taking orders.`);
      }
      return result;
    };

    const setButtonLoading = (button, loading, success) => {
      if (!button) return;
      const label = button.querySelector('span:not(.loading__spinner):not(.svg-wrapper)') || button;
      if (!button.dataset.skaiiyOriginalLabel) button.dataset.skaiiyOriginalLabel = (label.textContent || 'Add to cart').trim();
      button.disabled = loading;
      button.setAttribute('aria-disabled', loading ? 'true' : 'false');
      label.textContent = loading ? 'ADDING BUNDLE...' : (success ? 'BUNDLE + DISCOUNT ADDED' : button.dataset.skaiiyOriginalLabel);
      if (!loading && success) {
        window.setTimeout(() => {
          label.textContent = button.dataset.skaiiyOriginalLabel;
          button.disabled = false;
          button.setAttribute('aria-disabled', 'false');
        }, 1100);
      }
    };

    const addBundleToCart = async ({ bundle, button, checkout = false }) => {
      if (state.adding) return;
      clearErrors();
      const validation = validateBundle(bundle);
      if (!validation.valid) {
        showError(validation.message);
        const visibleBuilder = bundle.querySelector('[data-skaiiy-bundle-builder]');
        if (visibleBuilder) visibleBuilder.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (typeof validation.emptySlot === 'number') openPicker(bundle, validation.emptySlot);
        return;
      }

      let items;
      try {
        items = buildCartItems(bundle);
      } catch (error) {
        showError(error.message || 'The bundle selection is incomplete.');
        console.error('Skaiiy bundle payload validation failed:', error);
        return;
      }

      state.adding = true;
      setButtonLoading(button, true, false);
      let addedCartResult = null;
      let activeCartElement = null;
      try {
        const payload = { items };
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        activeCartElement = cart;
        if (cart && typeof cart.getSectionsToRender === 'function') {
          payload.sections = cart.getSectionsToRender().map(section => section.id);
          payload.sections_url = window.location.pathname;
        }

        const shopRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root
          : '/';
        const cartAddUrl = `${shopRoot.replace(/\/?$/, '/')}cart/add.js`;
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        const responseText = await response.text();
        let result = {};
        try {
          result = responseText ? JSON.parse(responseText) : {};
        } catch (parseError) {
          throw new Error('Shopify returned an unreadable cart response.');
        }
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'The bundle could not be added.');
        }

        const returnedItems = Array.isArray(result.items) ? result.items : [];
        if (returnedItems.length === 0) {
          throw new Error('Shopify did not confirm the bundle items. Please try again.');
        }
        addedCartResult = result;

        const discountResult = await applyBundleDiscount({
          code: state.discountCode,
          cart,
          shopRoot
        });
        const finalCartResult = discountResult && typeof discountResult === 'object' ? discountResult : result;

        if (cart && typeof cart.renderContents === 'function' && finalCartResult.sections) {
          try {
            cart.renderContents(finalCartResult);
          } catch (renderError) {
            console.warn('Bundle and discount were applied, but the cart drawer could not refresh automatically:', renderError);
          }
        }

        const eventDetail = {
          cart: finalCartResult,
          items,
          quantity: state.quantity,
          discountCode: state.discountCode,
          discountPercent: state.discount,
          source: 'skaiiy-bundle',
          version: SKAIIY_BUNDLE_VERSION
        };
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: eventDetail }));
        document.dispatchEvent(new CustomEvent('cart:add', { detail: eventDetail }));
        window.dispatchEvent(new CustomEvent('cart:updated', { detail: eventDetail }));

        setButtonLoading(button, false, true);
        if (checkout) {
          window.location.assign(`${shopRoot.replace(/\/?$/, '/')}checkout`);
          return;
        }
        if (cart && typeof cart.open === 'function') {
          window.setTimeout(() => cart.open(), 30);
        }
      } catch (error) {
        console.error('Skaiiy mix-and-match bundle purchase failed:', error);
        if (addedCartResult) {
          if (activeCartElement && typeof activeCartElement.renderContents === 'function' && addedCartResult.sections) {
            try {
              activeCartElement.renderContents(addedCartResult);
            } catch (renderError) {
              console.warn('The bundle products were added, but the cart drawer could not refresh:', renderError);
            }
          }
          if (activeCartElement && typeof activeCartElement.open === 'function') {
            window.setTimeout(() => activeCartElement.open(), 30);
          }
          showError(error.message || 'The bundle products were added, but the Shopify discount could not be applied.');
        } else {
          showError(error.message || 'The bundle could not be added. Please try again.');
        }
        setButtonLoading(button, false, false);
      } finally {
        state.adding = false;
      }
    };

    const setupPickerPortal = bundle => {
      const overlay = bundle.querySelector('[data-skaiiy-picker-overlay]');
      const picker = bundle.querySelector('[data-skaiiy-bundle-picker]');
      if (!overlay || !picker) return;
      const portal = document.createElement('div');
      portal.className = 'skaiiy-native-bundle__portal';
      portal.setAttribute('data-skaiiy-bundle-portal', '');
      portal.appendChild(overlay);
      portal.appendChild(picker);
      document.body.appendChild(portal);
      pickerParts.set(bundle, { portal, overlay, picker });

      overlay.addEventListener('pointerdown', event => {
        event.preventDefault();
        closePicker(bundle);
      });

      picker.addEventListener('click', async event => {
        const closeButton = event.target.closest('[data-skaiiy-picker-close]');
        if (closeButton) {
          event.preventDefault();
          closePicker(bundle);
          return;
        }
        const backButton = event.target.closest('[data-skaiiy-picker-back]');
        if (backButton) {
          event.preventDefault();
          showProductStep(bundle, picker, { resetCollection: false });
          return;
        }
        const resetButton = event.target.closest('[data-skaiiy-progressive-reset]');
        if (resetButton) {
          event.preventDefault();
          resetFlowStyle(state.activeFlow);
          renderPickerFlow(picker);
          return;
        }
        const productButton = event.target.closest('[data-skaiiy-pick-product]');
        if (productButton) {
          event.preventDefault();
          const product = state.pickerResults[Number(productButton.dataset.resultIndex)];
          if (product) await showVariantStep(bundle, picker, product);
          return;
        }
        const confirmButton = event.target.closest('[data-skaiiy-picker-confirm]');
        if (confirmButton) {
          event.preventDefault();
          if (!state.activeProduct || !state.activeFlow || !state.activeFlow.variant || state.activeSlotIndex == null) return;
          state.additional[state.activeSlotIndex - 1] = {
            productId: String(state.activeProduct.id),
            variantId: Number(state.activeFlow.variant.id)
          };
          clearErrors();
          closePicker(bundle);
          render();
        }
      });

      picker.addEventListener('input', event => {
        if (event.target.matches('[data-skaiiy-picker-search]')) queueProductSearch(picker);
      });

      picker.addEventListener('change', event => {
        if (event.target.matches('[data-skaiiy-picker-collection]')) {
          loadPickerCatalog(bundle, picker);
          return;
        }
        if (event.target.matches('[data-skaiiy-progressive-select]')) {
          if (state.activeFlow && state.activeFlow.currentIndex != null) {
            chooseFlowValue(state.activeFlow, event.target.value);
            renderPickerFlow(picker);
          }
        }
      });
    };

    bundleRoots.forEach(bundle => {
      setupPickerPortal(bundle);
      bundle.addEventListener('click', event => {
        const option = event.target.closest('[data-skaiiy-bundle-option]');
        if (option && bundle.contains(option)) {
          event.preventDefault();
          setTier(option);
          return;
        }
        const openButton = event.target.closest('[data-skaiiy-open-picker]');
        if (openButton && bundle.contains(openButton)) {
          event.preventDefault();
          openPicker(bundle, Number(openButton.dataset.slotIndex));
          return;
        }
        const addCurrentButton = event.target.closest('[data-skaiiy-add-current-product]');
        if (addCurrentButton && bundle.contains(addCurrentButton)) {
          event.preventDefault();
          const slotIndex = Number(addCurrentButton.dataset.slotIndex);
          const current = getCurrentSelection(bundle);
          const meta = getOptionMeta(current.product);
          const styleValue = meta.styleIndex != null ? optionValue(current.variant, meta.styleIndex) : '';
          state.additional[slotIndex - 1] = {
            productId: String(current.productId),
            variantId: null,
            repeatCurrent: true,
            lockedStyleValue: styleValue
          };
          clearErrors();
          render();
          window.setTimeout(() => {
            const select = bundle.querySelector(`[data-skaiiy-repeat-variant-select][data-slot-index="${slotIndex}"]`);
            if (select) select.focus({ preventScroll: true });
          }, 20);
          return;
        }
        const editButton = event.target.closest('[data-skaiiy-edit-slot-variant]');
        if (editButton && bundle.contains(editButton)) {
          event.preventDefault();
          const slotIndex = Number(editButton.dataset.slotIndex);
          const selection = getAdditionalSelection(bundle, slotIndex - 1);
          if (selection) openPicker(bundle, slotIndex, selection.product, { variantId: selection.variantId, lockStyle: true, allowStyleReset: true });
          return;
        }
        const removeButton = event.target.closest('[data-skaiiy-remove-slot]');
        if (removeButton && bundle.contains(removeButton)) {
          event.preventDefault();
          const slotIndex = Number(removeButton.dataset.slotIndex);
          if (slotIndex > 0) state.additional[slotIndex - 1] = null;
          clearErrors();
          render();
        }
      });

      bundle.addEventListener('change', event => {
        const repeatSelect = event.target.closest('[data-skaiiy-repeat-variant-select]');
        if (!repeatSelect || !bundle.contains(repeatSelect)) return;
        const slotIndex = Number(repeatSelect.dataset.slotIndex);
        const entry = state.additional[slotIndex - 1];
        if (!entry || !repeatSelect.value) return;
        entry.variantId = Number(repeatSelect.value);
        clearErrors();
        render();
      });
    });

    document.addEventListener('pointerdown', event => {
      if (!state.activePickerRoot) return;
      const { picker } = getParts(state.activePickerRoot);
      if (!picker) return;
      const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
      if (picker.contains(event.target) || path.includes(picker)) return;
      closePicker(state.activePickerRoot);
    }, true);

    const stopNativePurchase = event => {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    };

    document.addEventListener('click', event => {
      if (!isBundleActive()) return;
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const checkoutButton = target.closest(
        '[data-skaiiy-bundle-checkout], [data-dg-quick-checkout], [data-dg-more-payment-options], .shopify-payment-button__button, .shopify-payment-button__more-options'
      );
      if (checkoutButton && productRoot.contains(checkoutButton)) {
        stopNativePurchase(event);
        addBundleToCart({ bundle: getVisibleBundle(), button: checkoutButton, checkout: true });
        return;
      }

      const addButton = target.closest(
        'button[name="add"], .product-form__submit, .dg-mobile-product-panel__add, [data-skaiiy-bundle-add]'
      );
      if (!addButton || !productRoot.contains(addButton)) return;
      const form = addButton.closest('form[action*="/cart/add"]');
      if (!form) return;
      stopNativePurchase(event);
      addBundleToCart({ bundle: getVisibleBundle(), button: addButton, checkout: false });
    }, true);

    document.addEventListener('submit', event => {
      if (!isBundleActive()) return;
      const form = event.target instanceof Element ? event.target.closest('form[action*="/cart/add"]') : null;
      if (!form || !productRoot.contains(form)) return;
      stopNativePurchase(event);
      const button = event.submitter || form.querySelector('button[name="add"], .product-form__submit, .dg-mobile-product-panel__add, [type="submit"]');
      addBundleToCart({ bundle: getVisibleBundle(), button, checkout: false });
    }, true);

    const syncCurrentVariant = event => {
      const detail = event && event.detail ? event.detail : {};
      if (detail.sectionId && sectionId && String(detail.sectionId) !== sectionId) return;
      if (detail.variant && detail.variant.id) state.currentVariantOverride = detail.variant;
      window.setTimeout(render, 20);
    };

    productRoot.addEventListener('change', event => {
      if (!event.target.closest('variant-selects, variant-radios, variant-picker, .product-form__input, form[action*="/cart/add"]')) return;
      window.setTimeout(render, 30);
      window.setTimeout(render, 220);
    }, true);

    document.addEventListener('dg:variant-price-updated', syncCurrentVariant);
    document.addEventListener('dg:desktop-variant-updated', syncCurrentVariant);
    document.addEventListener('variant:change', syncCurrentVariant);

    document.addEventListener('keydown', event => {
      if (!state.activePickerRoot) return;
      const { picker } = getParts(state.activePickerRoot);
      if (!picker) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closePicker(state.activePickerRoot);
        return;
      }
      if (event.key === 'Tab') {
        const focusable = focusableElements(picker);
        if (!focusable.length) {
          event.preventDefault();
          picker.focus();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    ensureSelectionLength();
    render();
  };

  const init = scope => {
    const source = scope || document;
    const roots = new Set();
    if (source.matches && source.matches(PRODUCT_ROOT_SELECTOR)) roots.add(source);
    if (source.querySelectorAll) {
      source.querySelectorAll(PRODUCT_ROOT_SELECTOR).forEach(root => roots.add(root));
      source.querySelectorAll(BUNDLE_SELECTOR).forEach(bundle => {
        const root = bundle.closest(PRODUCT_ROOT_SELECTOR);
        if (root) roots.add(root);
      });
    }
    roots.forEach(initializeProduct);
  };

  window.SkaiiyNativeQuantityBundle = { init };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
